import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import ChatIcon from "@mui/icons-material/Chat";
import PeopleIcon from "@mui/icons-material/People";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { getPlatformStats } from "../api/adminApi";
import StatCard from "../components/StatCard";
import ChartCard from "../components/ChartCard";
import DateRangeToggle from "../components/DateRangeToggle";

const PIE_COLORS = ["#6366f1", "#e2e8f0"];

const WebsiteOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");

  useEffect(() => {
    setLoading(true);
    getPlatformStats("website", days).then(setStats).catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const dailyData = stats?.dailyConversations?.labels?.map((label, i) => ({
    date: label.slice(5),
    conversations: stats.dailyConversations.values[i],
  })) || [];

  const activeVsTotal = [
    { name: "Active", value: stats?.activeConversations || 0 },
    { name: "Inactive", value: Math.max(0, (stats?.totalConversations || 0) - (stats?.activeConversations || 0)) },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
        <Box>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Website</Typography>
          <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>Website widget platform overview</Typography>
        </Box>
        <DateRangeToggle value={days} onChange={setDays} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: "27px", mb: 5 }}>
        <StatCard icon={<PublicIcon sx={{ color: "#6366f1", fontSize: 22 }} />} label="Active Conversations" value={stats?.activeConversations || 0} color="#6366f1" />
        <StatCard icon={<ChatIcon sx={{ color: "#10b981", fontSize: 22 }} />} label="Total Conversations" value={stats?.totalConversations || 0} color="#10b981" />
        <StatCard icon={<PeopleIcon sx={{ color: "#f59e0b", fontSize: 22 }} />} label="Total Users" value={stats?.totalUsers || 0} color="#f59e0b" />
        <StatCard icon={<SmartToyIcon sx={{ color: "#8b5cf6", fontSize: 22 }} />} label="Active Bots" value={stats?.totalBots || 0} color="#8b5cf6" />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: "27px", mb: 5 }}>
        <ChartCard title="Conversation Trend" subtitle="Daily website conversations" height={300}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyData}>
              <defs>
                <linearGradient id="webColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }} />
              <Area type="monotone" dataKey="conversations" stroke="#6366f1" strokeWidth={2} fill="url(#webColor)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Active vs Total" subtitle="Conversation activity ratio" height={300}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={activeVsTotal} cx="50%" cy="50%" outerRadius={95} innerRadius={60} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {activeVsTotal.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", fontSize: 13 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </Box>
    </Box>
  );
};

export default WebsiteOverview;
